/**
 * Patient identifiers used to find and link patients. A mobile number is
 * required for every patient. An ABHA number or ABHA address is optional,
 * because not everyone has one.
 */
export type PatientIdentifier =
  | { kind: 'mobile'; value: string }
  | { kind: 'abhaNumber'; value: string }
  | { kind: 'abhaAddress'; value: string };

/**
 * Normalises an Indian mobile number to +91XXXXXXXXXX. Accepts spaces,
 * dashes, brackets and a +91, 91 or 0 prefix. Returns null when the input is
 * not a valid Indian mobile number.
 */
export function normalizeMobile(input: string): string | null {
  const compact = input.trim().replace(/[\s\-()]/g, '');
  let digits: string;
  if (compact.startsWith('+')) {
    // A leading + must be India's country code, so +98... is Iran, not a mobile.
    if (!/^\+91\d{10}$/.test(compact)) return null;
    digits = compact.slice(3);
  } else {
    if (!/^\d+$/.test(compact)) return null;
    digits = compact;
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  }
  if (!/^[6-9]\d{9}$/.test(digits)) return null;
  return `+91${digits}`;
}

/** Shows only the last four digits, for screens where the full number is not needed. */
export function maskMobile(normalized: string): string {
  return `+91 ••••••${normalized.slice(-4)}`;
}

/** Normalises a 14-digit ABHA number to XX-XXXX-XXXX-XXXX, or returns null. */
export function normalizeAbhaNumber(input: string): string | null {
  const trimmed = input.trim();
  if (!/^[\d\s-]+$/.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length !== 14) return null;
  return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}-${digits.slice(10)}`;
}

/** ABHA address domains: abdm in production, sbx in the ABDM sandbox. */
export const ABHA_ADDRESS_DOMAINS = ['abdm', 'sbx'] as const;

/**
 * Normalises an ABHA address such as name@abdm to lower case, or returns null.
 * The local-part rule here is deliberately permissive. Phase 13 aligns it with
 * ABDM's exact rules once the sandbox is available.
 */
export function normalizeAbhaAddress(input: string): string | null {
  const value = input.trim().toLowerCase();
  const match = /^([a-z0-9][a-z0-9._]{1,30}[a-z0-9])@([a-z]+)$/.exec(value);
  if (!match) return null;
  const domain = match[2] ?? '';
  if (!(ABHA_ADDRESS_DOMAINS as readonly string[]).includes(domain)) return null;
  return value;
}

/**
 * Reads whatever a doctor or front desk typed into one search box: an ABHA
 * address, a 14-digit ABHA number, or a mobile number.
 */
export function parsePatientIdentifier(input: string): PatientIdentifier | null {
  if (input.includes('@')) {
    const value = normalizeAbhaAddress(input);
    return value ? { kind: 'abhaAddress', value } : null;
  }
  const abhaNumber = normalizeAbhaNumber(input);
  if (abhaNumber) return { kind: 'abhaNumber', value: abhaNumber };
  const mobile = normalizeMobile(input);
  return mobile ? { kind: 'mobile', value: mobile } : null;
}
